import { Link } from 'react-router-dom';
import { ArrowRight, Wallet } from 'lucide-react';
import { Button, Card, CardContent, Separator } from '@mlm/ui';
import type { SlotWallet } from '../../lib/api';
import { fmtCurrency, fmtNumber } from '../../lib/format';

/**
 * The wallet rail of the passbook: Available balance as the focal number, a
 * Cash out action, then the secondary balances (pending, lifetime, and GC /
 * coins when the tenant uses them). Balances are positions, not movements, so
 * they render neutral — only the statement lines carry credit/debit hue.
 */
export function WalletPanel({
  wallet,
  slotLabel,
  coinLabel = 'Coins',
  hideCashOut = false,
  topupPayoffHref = null,
}: {
  wallet: SlotWallet;
  slotLabel?: string | null;
  coinLabel?: string;
  /** Hide the Cash out CTA when the panel is shown ON the Cash Out page. */
  hideCashOut?: boolean;
  /**
   * When set (and the slot has an open debt), the debt strip shows a
   * "Pay it off with a top-up" link to this route. Callers pass it only when
   * `walletTopupEnabled && cdTopupSettlesDebt`, so the CTA never dead-ends.
   */
  topupPayoffHref?: string | null;
}) {
  const secondary: { label: string; value: string }[] = [
    { label: 'Pending', value: fmtCurrency(wallet.pending) },
    // `totalEarned`, not `lifetimeEarned`: migrated members must keep the
    // lifetime figure their old system showed (it includes pre-migration
    // earnings that have no ledger rows here). Identical for native slots.
    { label: 'Lifetime earned', value: fmtCurrency(wallet.totalEarned) },
  ];
  if (Number(wallet.availableGc) > 0 || Number(wallet.lifetimeGcEarned) > 0) {
    secondary.push({ label: 'Gift certificates', value: fmtCurrency(wallet.availableGc) });
  }
  if (Number(wallet.availableCoin) > 0 || Number(wallet.lifetimeCoinEarned) > 0) {
    secondary.push({ label: coinLabel, value: fmtNumber(wallet.availableCoin) });
  }
  // Whole answers — a fractional credit isn't a thing.
  if (Number(wallet.availableAiCredit) > 0 || Number(wallet.lifetimeAiCreditEarned) > 0) {
    secondary.push({
      label: 'AI answers',
      value: String(Math.floor(Number(wallet.availableAiCredit))),
    });
  }
  const canCashOut = Number(wallet.available) > 0;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-2">
          <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Available balance
          </p>
          {slotLabel ? (
            <span className="truncate text-caption text-muted-foreground">{slotLabel}</span>
          ) : null}
        </div>

        <div className="mt-2 text-4xl font-semibold leading-none tabular-nums">
          {fmtCurrency(wallet.available)}
        </div>

        {hideCashOut ? null : canCashOut ? (
          <Button asChild className="mt-4 w-full">
            <Link to="/cash-out">
              Cash out <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <Button variant="outline" className="mt-4 w-full" disabled>
            Nothing to cash out yet
          </Button>
        )}

        <Separator className="my-4" />

        <dl className="space-y-2">
          {secondary.map((b) => (
            <div key={b.label} className="flex items-baseline justify-between gap-3 text-sm">
              <dt className="text-muted-foreground">{b.label}</dt>
              <dd className="font-medium tabular-nums">{b.value}</dd>
            </div>
          ))}
        </dl>

        {wallet.debt && !wallet.debt.settledAt ? (
          <DebtStrip debt={wallet.debt} payoffHref={topupPayoffHref} />
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * CD/Half-CD debt progress. Earnings pay the debt before reaching the wallet;
 * shown only while the debt is open (a settled slot is a normal paid slot).
 */
function DebtStrip({
  debt,
  payoffHref = null,
}: {
  debt: NonNullable<SlotWallet['debt']>;
  payoffHref?: string | null;
}) {
  const total = Number(debt.debtTotal);
  const paid = Number(debt.debtPaid);
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  return (
    <div className="mt-4 rounded-lg border bg-muted/40 p-3">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-muted-foreground">Kit debt remaining</span>
        <span className="font-medium tabular-nums">{fmtCurrency(debt.debtRemaining)}</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-caption text-muted-foreground">
        {fmtCurrency(debt.debtPaid)} of {fmtCurrency(debt.debtTotal)} paid — your earnings settle
        this first, then credit your wallet.
      </p>
      {payoffHref ? (
        <Button asChild variant="outline" size="sm" className="mt-3 w-full">
          <Link to={payoffHref}>
            Pay it off with a top-up <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

/** Pre-slot placeholder: no wallet yet, so don't fabricate numbers. */
export function WalletPanelEmpty() {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-6 text-sm text-muted-foreground">
        <Wallet className="mt-0.5 h-5 w-5 shrink-0" />
        <span>
          Your wallet opens once you activate a slot. Redeem a Code + PIN from your Vault to get
          started.
        </span>
      </CardContent>
    </Card>
  );
}
